// Move money — funds between the desk's own accounts (v2 §3.2).
//
// Operator picks From + To (both must hold the same asset). The asset is
// inferred from the From account, not entered separately. The fee is under
// More — most internal moves carry none.
//
// Sweep of 14 Sept 2026 (SWEEP-SCOPE §5.10): ScreenHeader with the subtitle
// on both devices and an About; the fold is "More"; the fee-payer chips
// name the two accounts and no longer offer "house" once a fee is typed (the
// API records NO fee for house — the 3 Sept fault); the success line names
// the amount, both accounts and the reference; figures clear on success.
// The posting body is as it was: fee_payer is 'house' while no fee is typed.
//
// 16 Sept 2026 (operator, on this screen with eleven hot-wallet → exchange
// sends to book: "how can i select and record the move in 1 click?"): the
// URL now fills the whole form — ?to=&toAsset= (the To account), ?amount=,
// ?fee= (the From account pays it), ?when= (Dubai wall-clock) and ?note= —
// read once at mount exactly as From is; a Chain row that is an exchange
// movement, and the Exchanges Movements tab, build that URL. The screen also
// DATES the entry: As-of chips between the amount and More (the time that
// arrived with the screen is its own lit chip), sent as `effective_at` —
// absent means now, as everywhere. One read-back line above the button says
// what will post, in the accounts' names, only when everything needed is in.
//
// 21 Oct 2026 audit (operator: "I need clean view for operator"): To no longer
// offers the From account (the "From and To must differ" refusal stays as the
// backstop); the Amount row names the asset once, in its label; the read-back
// is one full-contrast line — amount, the two accounts, the fee and who pays
// it, each said once; the two pickers keep one trigger height.
//
// 22 Oct 2026 design-system sweep: the accounts read FAILING is said (both pickers stood empty with no word and, opened, said "No
// such account"), the pickers wait while it is read, a refusal is its first sentence with the rest behind Details, a deduped
// answer is one line with the reference behind Details. The posting body and every refusal are as they were.

import { useState, useMemo, useEffect, useRef } from 'react';
import { DeskSwitch, useDesk } from '../components/DeskSwitch';
import { isSentinel } from '../lib/errors';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, type TreasuryRow } from '../lib/api';
import { tg } from '../lib/telegram';
import { treasuryPath, makeReference, DEFAULT_RAILS } from '../lib/workflow';
import { ScreenHeader } from '../components/ScreenHeader';
import { RailPicker, type RailValue } from '../components/RailPicker';
import { RepeatPanel, useRepeatGuard } from '../components/RepeatPanel';
import { MoreDetails } from '../components/MoreDetails';
import { RecentEntries } from '../components/RecentEntries';
import { Field, FieldRow } from '../components/Field';
import { ErrorLine } from '../components/ErrorLine';
import { ReadFailed, ServerLine, StateLine, TAP } from '../components/kit';
import { amountOk, amountProblem, optionalAmountProblem } from '../lib/amount';
import { AsOfChips } from '../components/AsOfChips';
import { displayAsset, fmtAmount, dubaiWallToIso, backdateProblem } from '../lib/format';
import { railName, sameAsset } from '../lib/naming';

type FeePayer = 'sender' | 'receiver' | 'house';

/** The two payers a typed fee can have, in the operator's words. 'house' is
 *  not offered: the API records NO fee for it, so a network fee the chain
 *  burned would stay in the books (3 Sept audit). The values are the API's. */
const FEE_PAYERS: ReadonlyArray<{ v: Exclude<FeePayer, 'house'>; l: string }> = [
  { v: 'sender',   l: 'From account pays' },
  { v: 'receiver', l: 'To account pays'   },
];

/** One trigger height for both pickers (21 Oct 2026 audit): a picked account
 *  (From, 34px at lg) and an empty one (To, 40px) differ in RailPicker's own
 *  trigger, so the two boxes side by side stood at two heights. The shared
 *  picker is not this screen's file; this floors both boxes at the empty
 *  one's height — 42px on the phone, 40px from lg. */
const PICKER_HEIGHT = '[&_button.input]:min-h-[42px] lg:[&_button.input]:min-h-[40px]';

/** The accounts the To picker offers: the ones holding From's asset — a move
 *  is same-asset, the same coin on another chain is two moves — and never From
 *  itself (21 Oct 2026 audit: it was listed, then refused with "From and To
 *  must differ"; that refusal below stays as the backstop). From stays in the
 *  list only while it is To's CURRENT value, so a conflict that arises when
 *  From is changed onto To keeps its account name on the box and the refusal
 *  under it, instead of a bare slug. */
export function toAccounts(rows: TreasuryRow[], from: RailValue, to: RailValue): TreasuryRow[] {
  const sameAssetRows = from.asset ? rows.filter((r) => sameAsset(r.asset, from.asset)) : rows;
  const is = (r: TreasuryRow, v: RailValue) => !!v.slug && r.slug === v.slug && sameAsset(r.asset, v.asset);
  return sameAssetRows.filter((r) => !is(r, from) || is(r, to));
}

export function TreasuryRebalance() {
  const nav = useNavigate();

  // From defaults to office cash (the 95% start point for treasury moves);
  // operator picks To. Easy to override per session. An own-account page's
  // "Move money from this account" door lands here with ?rail=&railAsset=
  // (the contract Record money already honours), and that account is From.
  const [search] = useSearchParams();
  const prefill = ((): RailValue | null => {
    const slug = search.get('rail'), asset = search.get('railAsset');
    return slug && asset ? { slug, asset } : null;
  })();
  // THE WHOLE FORM FROM THE URL (16 Sept 2026): a Chain row that is an
  // exchange movement, or the Movements tab, lands here with To, the amount,
  // the venue's fee, the block time and the note as well. Each is read ONCE,
  // as the initial state — the same way From is — so nothing here ever
  // fights what the operator then types.
  const prefillTo = ((): RailValue | null => {
    const slug = search.get('to'), asset = search.get('toAsset');
    return slug && asset ? { slug, asset } : null;
  })();
  const urlFee  = search.get('fee') ?? '';
  const urlWhen = search.get('when') ?? '';
  const [fromRail, setFromRail] = useState<RailValue>(prefill ?? { ...DEFAULT_RAILS.cash });
  const [toRail,   setToRail]   = useState<RailValue>(prefillTo ?? { slug: '', asset: '' });
  // WHICH DESK'S ACCOUNTS — DubaiOTC only since the IndiaOTC wind-down (26
  // Sept 2026, components/DeskSwitch.tsx); the India banks this screen once
  // reached (Federal, RBL and the rest under treasury:indiaotc:bank, added 5
  // Sept 2026) are gone, written off in 2ce2119. Same-entity both sides: a
  // move between desks is a capital movement, not a rebalance, and has its
  // own door.
  const [entity, setEntity] = useDesk();
  const [amount, setAmount] = useState(search.get('amount') ?? '');
  const [fee, setFee] = useState(urlFee);
  // 'house' means NO FEE IS RECORDED — the API forces the amount to zero for it.
  // It used to be the default, so a network fee the operator typed was silently
  // dropped and the coins the chain burned never left the books (3 Sept audit).
  // A fee that arrives with the URL is the venue's: the From account pays it.
  const [feePayer, setFeePayer] = useState<FeePayer>('sender');
  const [notes, setNotes] = useState(search.get('note') ?? '');
  // AS OF — when the money moved (16 Sept 2026): Dubai wall-clock, '' = now,
  // the pattern Record money keeps. ?when= is the chain's block time and
  // shows as its own chip; the instant goes out as `effective_at`.
  const [when, setWhen] = useState(urlWhen);
  const whenIso   = when ? dubaiWallToIso(when) : '';
  const whenWords = backdateProblem(when);

  const treasury = useQuery({
    queryKey: ['treasury', 'balances', entity],
    queryFn:  () => api.treasuryBalances(entity),
  });
  const rows = treasury.data?.rows ?? [];

  // To's allowed assets must match From's asset: a move is same-asset. The
  // same coin on another chain is two moves (the chain-swap instrument was
  // designed out of the fresh books). And never the From account itself
  // (toAccounts, above — 21 Oct 2026 audit).
  const toRows = useMemo(
    () => toAccounts(rows, fromRail, toRail),
    [rows, fromRail, toRail],
  );

  // Switching desk empties both pickers: the office-cash default is DubaiOTC's
  // and every India rail is a different account. Runs only on a CHANGE — the
  // previous desk is kept in state and compared during render (React's
  // "adjust state when a prop changes" pattern), so the mount never wipes
  // the prefilled account, and StrictMode's double effect run cannot either
  // (a first-run ref did, once mounted twice — Phase D review, 14 Sept 2026).
  const [prevEntity, setPrevEntity] = useState(entity);
  if (entity !== prevEntity) {
    setPrevEntity(entity);
    setFromRail(entity === 'dubaiotc' ? { ...DEFAULT_RAILS.cash } : { slug: '', asset: '' });
    setToRail({ slug: '', asset: '' });
  }

  // If operator changes From and the current To no longer matches, clear it.
  useEffect(() => {
    if (toRail.asset && fromRail.asset && !sameAsset(toRail.asset, fromRail.asset)) {
      setToRail({ slug: '', asset: '' });
    }
  }, [fromRail.asset]);   // eslint-disable-line react-hooks/exhaustive-deps

  const feeNum = Number(fee) || 0;

  // WHAT WAS TYPED, CHECKED BEFORE IT IS SENT. The gate here was `!amount`, so
  // '1,000' went out as `"amount": null` and came back as "The books hit a
  // fault: toMinor: not a plain decimal string" — a 500 for a comma. The fee
  // box has the same shape and the same history: a fee typed as "0,5" folded to
  // zero and the coins the chain burned never left the books.
  const amountWords = amountProblem(amount);
  const feeWords    = optionalAmountProblem(fee);

  const sameRail = fromRail.slug === toRail.slug
                 && sameAsset(fromRail.asset, toRail.asset)
                 && fromRail.slug !== '';

  // The account names as the picker shows them — for the success line.
  const nameOf = (v: RailValue) =>
    railName(rows.find((r) => r.slug === v.slug && sameAsset(r.asset, v.asset)) ?? { slug: v.slug });

  // Everything the button needs, in one word: both accounts, a good amount,
  // no bad fee, no bad date, From ≠ To. The read-back line and the button
  // read the same answer.
  const ready = amountOk(amount) && feeWords === null && whenWords === null
             && !!fromRail.slug && !!toRail.slug && !sameRail;

  // Move money meets the same repeat guard Record money does — the panel and
  // the confirm are shared (components/RepeatPanel.tsx).
  const { twin: repeat, clearTwin, guard } = useRepeatGuard();

  // THE REFERENCE IS THE IDEMPOTENCY KEY, SO IT MUST SURVIVE A RETRY — the
  // fix Record money got on 5 Sept 2026, arriving here on 14 Sept (sweep item
  // 30). It was minted inside mutationFn, which runs on EVERY submit: a
  // timeout after the BFF had posted, Move money pressed again, and the
  // second attempt carried a fresh REBAL-… the ledger could not recognise as
  // the same move, so it posted twice. Held in a ref, not state: it must not
  // re-render, and it must not reset when a field is edited mid-retry.
  // Minted on the first submit, cleared on success. No prefix or day rides
  // on this screen's fields, so nothing here retires it early.
  const refRef = useRef<string | null>(null);
  // What the last posting said — the reference that landed, whether the BFF
  // deduped it (a retry whose first attempt had landed: nothing moved on THIS
  // request), and the facts the success line reads back.
  const [posted, setPosted] = useState<{ reference: string; deduped: boolean; amount: number; asset: string; from: string; to: string } | null>(null);
  const submit = useMutation({
    mutationFn: async (opts?: { confirmRepeat?: boolean }) => {
      refRef.current ??= makeReference('REBAL');
      const reference = refRef.current;
      const data = await guard(() => api.transfer({
        entity,
        source:      treasuryPath(fromRail.slug, fromRail.asset, entity),
        destination: treasuryPath(toRail.slug,   toRail.asset,   entity),
        amount:      { asset: fromRail.asset, amount: Number(amount) },
        fee_payer:   feeNum > 0 ? feePayer : 'house',
        fee:         feeNum > 0 ? { asset: fromRail.asset, amount: feeNum } : undefined,
        fee_account: `@expense:${entity}:network_fee`,
        reference,
        notes:       notes || undefined,
        confirm_repeat: opts?.confirmRepeat || undefined,
        // Only when set — absent means now (the transfers route's one door,
        // the same Record money sends through).
        effective_at: whenIso || undefined,
      }));
      return { data, reference, amount: Number(amount), asset: fromRail.asset, from: nameOf(fromRail), to: nameOf(toRail) };
    },
    onSuccess: ({ data, reference, ...facts }) => {
      clearTwin();
      refRef.current = null;
      setPosted({ reference: data.reference ?? reference, deduped: !!data.deduped, ...facts });
      // The figures leave with the entry (§7 rule 12); the two accounts stay.
      // The date leaves too (Record money's 5 Sept lesson): the next move
      // typed straight after a back-dated one must not go out on that day.
      setAmount(''); setFee(''); setNotes(''); setWhen('');
      tg()?.HapticFeedback.notificationOccurred('success');
      // Booked FROM a chain row (?note=chain …): fire the matcher now so the
      // pending row is gone before the operator looks back — the same door
      // Record money keeps (operator, 2 Sept: "make it auto vanish once
      // booked and matched"). Fire-and-forget; a failed sync just leaves the
      // row for the Chain screen's own Refresh.
      if ((search.get('note') ?? '').startsWith('chain ')) {
        api.chainSync().catch(() => {});
      }
    },
    onError:   (e) => { if (!isSentinel(e)) tg()?.HapticFeedback.notificationOccurred('error'); },
  });

  return (
    <div className="space-y-3 lg:max-w-[720px] lg:mx-auto lg:space-y-4">
      <button onClick={() => nav(-1)} className="btn-link text-meta">← Back</button>
      <ScreenHeader
        title="Move money"
        subtitle="Between the desk's own accounts — same asset both sides."
        help={
          <>
            <p>
              What one desk account holds, moved to another of the desk&rsquo;s accounts — Office cash to a
              bank, the hot wallet to an exchange. Nothing is earned and no party is involved. The same
              coin on another chain is two moves, one per chain.
            </p>
            <p>
              A network fee (under More) leaves the account you name and is booked as a cost. Every move
              carries one reference, so a retry after a timeout cannot post twice.
            </p>
          </>
        }
      >
        {/* Which desk — the shared switch, shown only when this admin can see more than one. */}
        <DeskSwitch value={entity} onChange={setEntity} />
      </ScreenHeader>

      <FieldRow>
        <Field label="From" className={PICKER_HEIGHT}>
          <RailPicker
            value={fromRail}
            onChange={setFromRail}
            rows={rows}
            loading={treasury.isFetching || treasury.fetchStatus === 'paused'}
            readFailed={treasury.isError}
          />
        </Field>

        {/* Both pickers wear RailPicker's own placeholder, "Pick an account":
            the label already says which side. To lists only accounts holding
            From's asset once From is picked (picked first, it lists them all),
            and never the From account itself. */}
        <Field label="To" className={PICKER_HEIGHT}>
          <RailPicker
            value={toRail}
            onChange={setToRail}
            rows={toRows}
            loading={treasury.isFetching || treasury.fetchStatus === 'paused'}
            readFailed={treasury.isError}
          />
        </Field>
      </FieldRow>
      {/* A READ THAT FAILED IS NOT AN EMPTY LIST (22 Oct 2026 sweep): with the accounts unread both pickers stood
          there with no balance and no word — and, opened, said "No such account". Said once, with the way out. */}
      {treasury.isError && <ReadFailed what="the accounts" error={treasury.error} onRetry={() => void treasury.refetch()} />}

      {/* The asset is named once, in the label (21 Oct 2026 audit: the label,
          a grey box repeating it beside the amount and the From chip said it
          three times). The box beside the amount is gone. */}
      <FieldRow>
        <Field label={fromRail.asset ? `Amount in ${displayAsset(fromRail.asset)}` : 'Amount'}>
          <input
            className="input"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
          />
          <ErrorLine>{amountWords}</ErrorLine>
        </Field>
      </FieldRow>

      {/* AS OF — when the money moved. The chips render the echo and the
          back-date words themselves; `whenIso` / `whenWords` stay this
          screen's. A time that arrived with the URL is its own lit chip. */}
      <AsOfChips preset={urlWhen || undefined} value={when} onChange={setWhen} disabled={submit.isPending} />

      {/* MORE — the network fee (0 of 2 moves) and Notes (0 of 2). Open
          from the start only when a fee arrived with the URL, so the
          operator sees the figure that will post. */}
      <MoreDetails label="More" defaultOpen={urlFee !== ''}>
        <Field label="Network fee" hint="optional">
          <input
            className="input"
            value={fee}
            onChange={(e) => setFee(e.target.value)}
            placeholder="0"
            inputMode="decimal"
          />
          <ErrorLine>{feeWords}</ErrorLine>
          {/* A fee always shows its payer: it changes what posts. On the
              phone the two segments split the width at the size every other
              segmented control has (.seg's own 14px); lg keeps its 12px. */}
          {feeNum > 0 && (
            <div className="flex gap-2 mt-2 lg:inline-flex lg:w-auto lg:gap-1 lg:p-1 lg:rounded-lg lg:border lg:border-stroke lg:bg-panel">
              {FEE_PAYERS.map((p) => (
                <button
                  key={p.v}
                  type="button"
                  onClick={() => setFeePayer(p.v)}
                  className={`seg whitespace-nowrap text-body lg:text-meta ${feePayer === p.v ? 'seg-on' : ''}`}
                >
                  {p.l}
                </button>
              ))}
            </div>
          )}
        </Field>
        <Field label="Notes">
          <textarea className="input h-16" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything to remember" />
        </Field>
      </MoreDetails>

      <ErrorLine>{sameRail ? 'From and To must differ' : null}</ErrorLine>

      {/* WHAT WILL POST, in the accounts' names — only once everything the
          button needs is in, so a filled-in form from a Chain row reads
          back as one line before the tap.
          21 Oct 2026 audit: it was the dimmest text on the page and said the
          asset three times ("Moves 150,000 USDT·TRC from … to …, fee 1.2
          USDT·TRC (… pays)"). Now full contrast, one line: the amount with
          its asset, the two accounts, the fee and who pays it (the chips'
          own words) — each fact once; the fee is in From's asset, as posted. */}
      {ready && (
        <p className="text-body text-tg-text">
          {fmtAmount(Number(amount), fromRail.asset)} {displayAsset(fromRail.asset)} · {nameOf(fromRail)} → {nameOf(toRail)}
          {feeNum > 0 && <> · fee {fmtAmount(feeNum, fromRail.asset)} ({feePayer === 'sender' ? 'From' : 'To'} account pays)</>}
        </p>
      )}

      {/* lg+: hairline separator + right-aligned, content-sized submit */}
      <div className="lg:flex lg:justify-end lg:border-t lg:border-stroke-soft lg:pt-5">
        <button
          className="btn-primary w-full lg:w-auto lg:px-6"
          disabled={submit.isPending || !ready}
          onClick={() => submit.mutate({})}
        >
          {submit.isPending ? 'Booking…' : 'Move money'}
        </button>
      </div>

      {/* A deduped answer says so: the ledger already held this reference and
          nothing moved on this request (the retry safety the held reference
          gives). Otherwise the line names the amount, both accounts and the
          reference, so the entry can be found. */}
      {submit.isSuccess && posted && (
        posted.deduped ? (
          <StateLine
            tone="warn"
            more={<>Recorded earlier under <span className="font-mono whitespace-nowrap">{posted.reference}</span> — check it in Money movements.</>}
            action={<Link to="/movements" className={TAP}>Money movements →</Link>}
          >
            Already recorded — nothing changed.
          </StateLine>
        ) : (
          // The same order as the read-back above the button — amount, then the two accounts — so the answer reads as the
          // question did; the asset is said once (22 Oct 2026 sweep: "AED Office cash" ran together).
          <p className="text-pos text-body">
            ✓ Moved {fmtAmount(posted.amount, posted.asset)} {displayAsset(posted.asset)} · {posted.from} → {posted.to}
            {' '}<span className="font-mono text-meta text-tg-hint whitespace-nowrap">{posted.reference}</span>
          </p>
        )
      )}
      {submit.isError && !isSentinel(submit.error) && <ServerLine error={submit.error} />}
      {repeat && (
        <RepeatPanel
          twin={repeat}
          pending={submit.isPending}
          onAlreadyBooked={() => { clearTwin(); nav(-1); }}
          onPostAnyway={() => submit.mutate({ confirmRepeat: true })}
        />
      )}

      {/* THE LAST TEN MOVES, newest first (operator, 16 Oct 2026: "Newest
          first"; ascending until then, by his 17 Sept words) — this desk's.
          Under the form, on
          its own read; it never holds the button. */}
      <RecentEntries kind="moves" entity={entity} title="Last 10 moves" />
    </div>
  );
}
